// PriorYearBanner — a PROMINENT, board-honest warning shown when the levy data
// on screen is for a PRIOR fiscal year than the one we are in right now. This is
// the orthogonal axis to StaleDataBanner: StaleDataBanner answers "is the pull
// recent?"; this answers "is this even the fiscal year we're in?". They stack.
//
// The failure this prevents: on 7/1 the fiscal year rolls to FY27, but the
// county doesn't load FY27 (fund 2119) into Oracle until mid-July. Until then the
// only data we hold is FY26/fund 2101. Without this banner the board reads
// last year's closing position as "today's" levy position. Renders nothing when
// the loaded data is the current FY (or nothing is loaded).
//
// Two variants, because "prior-year data on screen" has two very different
// causes and the copy must not lie about which one applies:
//   • 'not-loaded' (default) — the current FY genuinely isn't in Oracle yet;
//     the prior year is the ONLY data we hold.
//   • 'historical-source' — the view INTENTIONALLY reads complete fiscal years
//     (e.g. YoY comparisons over dash_finance_history, which ends at the last
//     closed FY by design). The current FY may well be loaded elsewhere — this
//     variant makes NO claim that the county hasn't loaded it.

import { CalendarClock } from "lucide-react";
import type { FyContext } from "../../lib/fiscal";

function fmtAsOf(asOf: string | null | undefined): string {
  if (!asOf) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
    const [y, m, d] = asOf.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return asOf;
}

export default function PriorYearBanner({
  ctx,
  asOf = null,
  variant = "not-loaded",
  className = "",
}: {
  /** The FY transition context (from `fyContext` / `fyContextFromAsOf`). */
  ctx: FyContext;
  /** The data's as-of date, if known — shown so the figure is self-dating. */
  asOf?: string | null;
  /**
   * Why prior-year data is on screen (see the header comment):
   * 'not-loaded' = current FY not in Oracle yet (default, preserves existing
   * callers); 'historical-source' = the view intentionally compares the latest
   * COMPLETE fiscal years — no claim about what the county has/hasn't loaded.
   */
  variant?: "not-loaded" | "historical-source";
  className?: string;
}) {
  if (!ctx.isPriorYear || !ctx.dataFy) return null;

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 bg-status-amber/12 border-status-amber/40 text-status-amber ${className}`}
    >
      <CalendarClock className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="text-sm">
        {variant === "historical-source" ? (
          <>
            <p className="font-semibold">
              Comparing {ctx.dataFy} figures — the latest complete fiscal year,
              not today’s position.
            </p>
            <p className="mt-1 text-theme-muted">
              We’re in {ctx.currentFy}. These comparisons use {ctx.dataFy} close
              data
              {asOf ? ` (as of ${fmtAsOf(asOf)})` : ""} because year-over-year
              math needs complete years on both sides — {ctx.currentFy}{" "}
              comparisons begin once the new year has enough months of activity.
            </p>
          </>
        ) : (
          <>
            <p className="font-semibold">
              Showing {ctx.dataFy} figures — this is the prior fiscal year, not
              today’s position.
            </p>
            <p className="mt-1 text-theme-muted">
              We’re in {ctx.currentFy}. The county hasn’t loaded {ctx.currentFy}{" "}
              levy data (fund {ctx.currentFund}) into Oracle yet, so the numbers
              below are {ctx.dataFy}
              {asOf ? ` (as of ${fmtAsOf(asOf)}, fund ${ctx.fund})` : ""} — its
              final / closing position. The new year’s appropriation typically
              loads in early–mid July.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
